
/**
 * useCustomerSubscriptions - Hook to manage customer subscriptions (v2)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { CustomerResource, SubscriptionsResponse } from '../../core/resources/customer';
import { useTagadaContext } from '../providers/TagadaProvider';
import { getGlobalApiClient } from './useApiQuery';

export interface UseCustomerSubscriptionsOptions {
    customerId?: string | null;
    enabled?: boolean;
}

export interface UseCustomerSubscriptionsResult {
    data: SubscriptionsResponse | null;
    isLoading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
    resumeSubscription: (subscriptionId: string) => Promise<{ success: boolean; error?: string }>;
    cancelSubscription: (subscriptionId: string) => Promise<{ success: boolean; error?: string }>;
}

export function useCustomerSubscriptions(
    options: UseCustomerSubscriptionsOptions = {}
): UseCustomerSubscriptionsResult {
    const { isSessionInitialized } = useTagadaContext();
    const queryClient = useQueryClient();

    const { customerId = null, enabled = true } = options;

    // Create customer resource client
    const customerResource = useMemo(() => {
        try {
            return new CustomerResource(getGlobalApiClient());
        } catch (error) {
            throw new Error(
                'Failed to initialize customer resource: ' +
                (error instanceof Error ? error.message : 'Unknown error')
            );
        }
    }, []);

    // Determine if query should be enabled
    const isEnabled = useMemo(() => {
        return Boolean(enabled && customerId && isSessionInitialized);
    }, [enabled, customerId, isSessionInitialized]);

    // Create query key
    const queryKey = useMemo(() => ['customer-subscriptions', { customerId }], [customerId]);

    // Fetch customer subscriptions using TanStack Query
    const {
        data = null,
        isLoading,
        error,
        refetch,
    } = useQuery<SubscriptionsResponse | null>({
        queryKey,
        enabled: isEnabled,
        queryFn: async () => {
            if (!customerId) {
                return null;
            }

            return await customerResource.getCustomerSubscriptions();
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
    });

    // Resume subscription mutation
    const resumeMutation = useMutation({
        mutationFn: async (subscriptionId: string) => {
            return await customerResource.resumeSubscription(subscriptionId);
        },
        onSuccess: () => {
            // Invalidate and refetch subscriptions
            void queryClient.invalidateQueries({ queryKey });
        },
    });

    // Cancel subscription mutation
    const cancelMutation = useMutation({
        mutationFn: async (subscriptionId: string) => {
            return await customerResource.cancelSubscription(subscriptionId);
        },
        onSuccess: () => {
            // Invalidate and refetch subscriptions
            void queryClient.invalidateQueries({ queryKey });
        },
    });

    // Resume subscription
    const resumeSubscription = useCallback(
        async (subscriptionId: string) => {
            try {
                await resumeMutation.mutateAsync(subscriptionId);
                return { success: true };
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to resume subscription';
                return { success: false, error: errorMessage };
            }
        },
        [resumeMutation]
    );

    // Cancel subscription
    const cancelSubscription = useCallback(
        async (subscriptionId: string) => {
            try {
                await cancelMutation.mutateAsync(subscriptionId);
                return { success: true };
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to cancel subscription';
                return { success: false, error: errorMessage };
            }
        },
        [cancelMutation]
    );

    return {
        data,
        isLoading,
        error,
        refetch: async () => {
            await refetch();
        },
        resumeSubscription,
        cancelSubscription,
    };
}

